import { FC } from 'react'
import styled, { keyframes } from 'styled-components'

const ProfileSkeleton: FC = () => {
  return (
    <Container>
      {/* Header bar */}
      <Header>
        <SkeletonLine width="80px" height="20px" />
      </Header>

      {/* Avatar + name row */}
      <ProfileRow>
        <SkeletonAvatar />
        <ProfileInfo>
          <SkeletonLine width="120px" height="18px" />
          <SkeletonLine width="160px" height="13px" style={{ marginTop: 6 }} />
        </ProfileInfo>
      </ProfileRow>

      {/* Favourite sports section */}
      <Section>
        <SkeletonLine width="130px" height="14px" style={{ marginBottom: 12 }} />
        <SportsRow>
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonSportCard key={i} />
          ))}
        </SportsRow>
      </Section>

      {/* Competitions card */}
      <SkeletonCard>
        <SkeletonLine width="70%" height="18px" style={{ marginBottom: 10 }} />
        <SkeletonLine width="50%" height="13px" style={{ marginBottom: 20 }} />
        <SkeletonButton />
      </SkeletonCard>
    </Container>
  )
}

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, var(--zinc-800, #27272a) 25%, var(--zinc-700, #3f3f46) 50%, var(--zinc-800, #27272a) 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;

  [data-theme="light"] & {
    background: linear-gradient(90deg, var(--border-primary) 25%, var(--bg-tertiary) 50%, var(--border-primary) 75%);
    background-size: 200px 100%;
  }
`

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: var(--color-screen-background, var(--bg-primary));
  min-height: 100vh;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--bg-tertiary);
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  height: 60px;
`

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 16px;
  background: var(--bg-tertiary);
  margin-bottom: 8px;
`

const SkeletonAvatar = styled(SkeletonBase)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  flex-shrink: 0;
`

const ProfileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const Section = styled.div`
  padding: 24px 16px 16px;
  background: var(--color-screen-background, var(--bg-primary));
`

const SportsRow = styled.div`
  display: flex;
  gap: 4px;
`

const SkeletonSportCard = styled(SkeletonBase)`
  min-width: 90px;
  height: 62px;
  border-radius: 12px;
`

const SkeletonCard = styled.div`
  margin: 8px 16px 24px;
  background: var(--bg-senary, var(--bg-tertiary));
  border-radius: 16px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SkeletonButton = styled(SkeletonBase)`
  width: 160px;
  height: 44px;
  border-radius: 8px;
`

const SkeletonLine = styled(SkeletonBase)<{ width: string; height: string }>`
  width: ${({ width }) => width};
  height: ${({ height }) => height};
`

export default ProfileSkeleton
